import { Button, useTranslation } from '@hbs-us/ui';
import { useState } from 'react';

import { estimateApprovalUrl } from '../exportLinks.js';

/**
 * Customer-approval LINK-OUT (UI-SPEC Interaction Contract — "public approval
 * stays SSR, the SPA surfaces the link only"; D-02 / A2 / Pitfall 2).
 *
 * This component is a copy/open link-builder to the LEGACY SSR public estimate
 * page (`/estimate/view/:token`) — it is NOT a re-implementation of the
 * customer sign+approve flow. That unauthenticated approve flow (which records
 * the approval and auto-converts the estimate into a job via
 * `convertApprovedEstimateToJob`) lives on the legacy SSR public host and is
 * NEVER re-mounted in the authenticated SPA.
 *
 * The host is read from `getEnvConfig` (the api/public host — never the literal
 * `domains.api` value hardcoded here) via `exportLinks.estimateApprovalUrl`,
 * which derives the legacy SSR public estimate host (T-21-07).
 *
 * When no public token has been minted yet, a pending notice is shown instead of
 * a broken link.
 */
export function PublicApprovalLink({ publicToken }: { publicToken: string | null }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const url = estimateApprovalUrl(publicToken);

  if (!url) {
    return <p className="text-muted-foreground text-sm">{t('estimates:approval.linkPending')}</p>;
  }

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="grid gap-2">
      <div className="font-semibold text-muted-foreground text-xs uppercase tracking-wide">
        {t('estimates:approval.linkHeading')}
      </div>
      <div className="break-all rounded-md border border-border bg-background px-3 py-2 font-mono text-foreground text-sm">
        {url}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" variant="secondary" onClick={() => void onCopy()}>
          {copied ? t('estimates:approval.copied') : t('estimates:approval.copyLink')}
        </Button>
        <Button asChild variant="secondary">
          <a href={url} target="_blank" rel="noreferrer">
            {t('estimates:approval.openPage')}
          </a>
        </Button>
      </div>
      <p className="text-muted-foreground text-xs">{t('estimates:approval.linkHelp')}</p>
    </div>
  );
}
